// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { SupportSnackbar } from '@/components/support-snackbar';
import { EditOutlined } from '@/constants/hardfin-standard-icons';
import { MembershipReportData } from '@/membership/api/membership-types';
import { MembersSerialsGrid } from '@/membership/components/members-serials-grid';
import { MembersSerialsToggle } from '@/membership/components/members-serials-toggle';
import { MembersView } from '@/membership/components/members-view';
import { MembershipRulesDialog } from '@/membership/components/membership-rules-dialog';
import {
  useMembershipRules,
  useMembershipScopeOptions,
  useRunMembershipRules,
} from '@/membership/hooks/use-membership';
import { useMembershipView } from '@/membership/hooks/use-membership-view';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';

// The expansion and the scroll share these, so they run as one movement. The
// curve is the ease-in-out the collapse transition uses.
const COLLAPSE_MS = 300;
const SCROLL_OFFSET_PX = 80;

function toEased(progress: number): number {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;
}

// The nearest ancestor that actually scrolls. The page scrolls the document in
// most layouts, but a tab inside a scrolling panel does not.
function toScrollParent(element: HTMLElement): HTMLElement {
  let parent = element.parentElement;
  while (parent !== null) {
    const overflow = getComputedStyle(parent).overflowY;
    if (
      (overflow === 'auto' || overflow === 'scroll') &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.scrollingElement as HTMLElement;
}

function toViewportTop(scroller: HTMLElement): number {
  return scroller === document.scrollingElement
    ? 0
    : scroller.getBoundingClientRect().top;
}

// The chart, its scrub bar, the rollforward and the delta chart together. The
// area is held at this height so a re-run does not collapse the page.
const REPORT_MIN_HEIGHT = 720;

interface Props {
  projectKey: string;
}

/**
 * A project's membership report. It opens on the answer rather than on a form,
 * because a project nobody has configured still has units to chart.
 */
export function MembershipReportTab(props: Props) {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [report, setReport] = useState<MembershipReportData | null>(null);
  const [isSerialsOpen, setIsSerialsOpen] = useState(false);
  const serialsRef = useRef<HTMLDivElement | null>(null);
  // The frame in flight, so a second toggle inside the animation replaces the
  // first rather than racing it, and unmounting stops it writing scrollTop on a
  // page that has moved on.
  const scrollFrameRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    []
  );

  // The browser's own smooth scroll runs on a curve and a duration of its own,
  // so starting it alongside the expansion still reads as two movements. This
  // steps the scroll frame by frame over the same duration and curve the
  // collapse uses, which is what makes them one movement.
  const scrollWithExpansion = () => {
    const element = serialsRef.current;
    if (element === null) return;
    const scroller = toScrollParent(element);
    const startedFrom = scroller.scrollTop;
    const target =
      startedFrom +
      element.getBoundingClientRect().top -
      toViewportTop(scroller) -
      SCROLL_OFFSET_PX;
    const startedAt = performance.now();

    if (scrollFrameRef.current !== null) {
      cancelAnimationFrame(scrollFrameRef.current);
    }

    const step = () => {
      const progress = Math.min(
        (performance.now() - startedAt) / COLLAPSE_MS,
        1
      );
      // Only a write. Reading how far the page can scroll would force a layout
      // on every frame while the height is still transitioning, which is what
      // makes the expansion stutter. A scrollTop past the end is clamped by the
      // browser anyway, and the page has grown by the next frame.
      scroller.scrollTop =
        startedFrom + (target - startedFrom) * toEased(progress);
      scrollFrameRef.current =
        progress < 1 ? requestAnimationFrame(step) : null;
    };
    scrollFrameRef.current = requestAnimationFrame(step);
  };
  const view = useMembershipView(report);

  const rulesQuery = useMembershipRules(props.projectKey);
  const optionsQuery = useMembershipScopeOptions(props.projectKey, true);
  const runMutation = useRunMembershipRules();

  // The failure lives in the mutation, so it describes the newest run and
  // nothing else: a rerun clears it, a success clears it, and a slow run that
  // fails after a newer one started never reaches the screen.
  const runFailure = runMutation.error;
  const runErrorMessage = runFailure?.userMessage ?? null;

  const rules = rulesQuery.data;
  const runReport = runMutation.mutate;
  const resetRun = runMutation.reset;

  // The chart is rebuilt every time the rules are fetched, not only when they
  // change, because the window comes from the project's own dates. Moving those
  // changes the answer without touching the rules.
  const fetchedAt = rulesQuery.dataUpdatedAt;
  // The run in flight, so a result that arrives after a newer run started is
  // dropped rather than drawn. Rules can change mid-flight and the answer to
  // the older question is not the one on screen.
  const runKeyRef = useRef('');
  useEffect(() => {
    if (rules === undefined) return;
    const runKey = `${props.projectKey}|${String(fetchedAt)}`;
    runKeyRef.current = runKey;
    resetRun();
    runReport(
      { projectKey: props.projectKey, definition: rules.definition },
      {
        onSuccess: (next) => {
          if (runKeyRef.current !== runKey) return;
          setReport(next);
        },
      }
    );
  }, [rules, fetchedAt, props.projectKey, runReport, resetRun]);

  if (rulesQuery.isError || optionsQuery.isError) {
    return (
      <Shell>
        <Alert severity="error">Membership could not be loaded</Alert>
      </Shell>
    );
  }
  if (
    rulesQuery.isPending ||
    optionsQuery.isPending ||
    rules === undefined ||
    optionsQuery.data === undefined
  ) {
    return (
      <Shell>
        <Loading label="Loading membership" />
      </Shell>
    );
  }

  return (
    <Shell
      actions={
        <Button
          size="small"
          startIcon={<EditOutlined />}
          onClick={() => {
            setIsEditorOpen(true);
          }}
        >
          Edit rules
        </Button>
      }
    >
      {/* A rejection the user can act on is shown here. Anything else is a
          support problem, so it reaches the snackbar instead of the tab. */}
      {runErrorMessage !== null && (
        <Alert severity="error">{runErrorMessage}</Alert>
      )}
      <SupportSnackbar
        isOpen={runFailure !== null && runErrorMessage === null}
        message="An error occurred while reading the membership report."
        onClose={runMutation.reset}
      />

      {/* Saving rules re-runs the report, and a chart that unmounts for a
          spinner collapses the page and snaps it back. The area holds its
          height so only its contents change. */}
      <Box sx={{ minHeight: REPORT_MIN_HEIGHT }}>
        {runFailure === null && report === null && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: REPORT_MIN_HEIGHT }}
          >
            <Loading label="Building the chart" />
          </Stack>
        )}

        {report !== null && (
          <MembersView
            report={report}
            view={view}
            serialsBelow={
              <>
                <MembersSerialsToggle
                  dayDate={view.dayDate}
                  isOpen={isSerialsOpen}
                  onToggle={() => {
                    setIsSerialsOpen(!isSerialsOpen);
                  }}
                />
                <Collapse
                  in={isSerialsOpen}
                  unmountOnExit
                  // The section expands downward, so its top is where it will
                  // be from the first frame. Scrolling on entering rather than
                  // on entered runs the two together instead of one after the
                  // other.
                  timeout={COLLAPSE_MS}
                  onEntering={scrollWithExpansion}
                >
                  <Paper
                    variant="outlined"
                    ref={serialsRef}
                    sx={{ mt: 1, p: 2 }}
                  >
                    <MembersSerialsGrid members={view.members} />
                  </Paper>
                </Collapse>
              </>
            }
          />
        )}
      </Box>

      <MembershipRulesDialog
        isOpen={isEditorOpen}
        projectKey={props.projectKey}
        scopeOptions={optionsQuery.data}
        definition={rules.definition}
        onClose={() => {
          setIsEditorOpen(false);
        }}
        onSaved={() => {
          setReport(null);
        }}
      />
    </Shell>
  );
}

function Shell(props: { actions?: ReactNode; children: ReactNode }) {
  return (
    <HardfinTabCardWrapper title="Membership" actions={props.actions}>
      <Stack gap={2} sx={{ pt: 2 }}>
        {props.children}
      </Stack>
    </HardfinTabCardWrapper>
  );
}

function Loading(props: { label: string }) {
  return (
    <Stack direction="row" gap={1} alignItems="center" sx={{ py: 2 }}>
      <CircularProgress size={18} />
      <Typography variant="body2" color="text.secondary">
        {props.label}
      </Typography>
    </Stack>
  );
}
